import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  deviceTypes,
  ORIGIN_SOURCE,
  ShimmerType,
} from '@nykaa/cart-shared/constants';
import ShimmerCard from '@nykaa/cart-shared/components/Shimmer/Card';
import PickFreeSamples from '@nykaa/cart-shared/components/PickFreeSamples';
import { EDD_EXPERIEMENT_HEADER } from '@nykaa/cart-shared/store/constants';
import {
  IFreeSampleCard,
  IOfferFreeSample,
} from '@nykaa/cart-shared/components/PickFreeSamples/types';
import ActionTypes from '@nykaa/cart-shared/store/actionTypes';
import { CART_TEXT_MESSAGES } from '@nykaa/cart-shared/constants/message';
import {
  AddOrRemoveSamplesType,
  FREE_SAMPLE_STATUS,
} from '@nykaa/cart-shared/constants';
import {
  addFreeSamples,
  removeFreeSamples,
} from '@nykaa/cart-shared/store/service';
import { setToastMessage } from '@nykaa/cart-shared/store/action';
import { freeSampleProductDataDTO } from '@nykaa/cart-shared/store/types/cart';
import {
  PickFreeSamplesShimmerContainer,
  PickYourFreeSamplesLayout,
} from 'layouts/ShoppingBag/styled';
import { State } from 'store/types';

interface IPickFreeSamplesScreenProps {
  togleShowPickFreeSamplesScreen: () => void;
  isConvenienceFeeEnabled?: boolean;
}

interface IAddRemoveProductParams {
  product: freeSampleProductDataDTO;
  offerCount?: {
    offerClaimedCount: number;
    offerEligibleCount: number;
  };
}

const PickFreeSample = ({
  togleShowPickFreeSamplesScreen,
  isConvenienceFeeEnabled,
}: IPickFreeSamplesScreenProps) => {
  const dispatch = useDispatch();
  const { pincode } = useSelector((state: State) => state.cartReducer.cartInfo);

  const freeSamplesInfo = useSelector(
    (state: State) => state.cartReducer.freeSamples
  );
  const { eligibleCount, freeSamplesDataDTOList } = freeSamplesInfo || {};
  const { ADDED_TO_BAG, REMOVE, DONE, ADD_TO_BAG } = CART_TEXT_MESSAGES;
  const eddWidget =
    useSelector((state: State) => state.remoteConfig.switches.cartEddWidget) ||
    false;

  const handleAddRemoveProduct = async (param: IAddRemoveProductParams) => {
    const { showProductStatus, productId, ruleId } = param.product;
    const { offerClaimedCount = 0, offerEligibleCount = 0 } =
      param.offerCount || {};
    if (
      !(
        showProductStatus === FREE_SAMPLE_STATUS.NOT_APPLIED &&
        offerClaimedCount === offerEligibleCount
      )
    ) {
      const type =
        showProductStatus == FREE_SAMPLE_STATUS.APPLIED
          ? AddOrRemoveSamplesType.remove
          : AddOrRemoveSamplesType.add;
      const params: any = {
        deviceType: deviceTypes.DESKTOP,
        pincode,
        type,
        items: [
          {
            productId,
            sourceRuleId: ruleId,
            quantity: 1,
          },
        ],
        originSource: ORIGIN_SOURCE.CART,
        headers: { [EDD_EXPERIEMENT_HEADER]: eddWidget },
        domain: __PLATFORM__,
        catalog_tag_filter: __PLATFORM__,
      };
      const handler =
        type === AddOrRemoveSamplesType.remove
          ? removeFreeSamples
          : addFreeSamples;

      try {
        const data = await handler(params, isConvenienceFeeEnabled);
        dispatch({
          type: ActionTypes.SET_FREE_SAMPLES,
          payload: data,
        });
      } catch (error) {
        const { message } = error as Error;
        setToastMessage(message)(dispatch);
      }
    }
  };

  if (!eligibleCount) {
    return (
      <PickFreeSamplesShimmerContainer>
        <ShimmerCard type={ShimmerType.PICK_FREE_SAMPLES} />
        <ShimmerCard type={ShimmerType.PICK_FREE_SAMPLES} />
      </PickFreeSamplesShimmerContainer>
    );
  }

  const offerSamples: IOfferFreeSample[] = freeSamplesDataDTOList?.map(
    (offerSample) => ({
      title: offerSample?.freeSampleMessage || '',
      offerCode: `Offer: ${offerSample?.offerText || ''}`,
      freeSampleCards: offerSample?.freeSampleProductDataDTOList?.map(
        (freeSample) => {
          const {
            showProductStatus,
            productId = '',
            name,
            imageUrl,
          } = freeSample;
          const {
            claimedCount: offerClaimedCount = 0,
            eligibleCount: offerEligibleCount = 0,
          } = offerSample;
          const tag =
            showProductStatus == FREE_SAMPLE_STATUS.APPLIED && ADDED_TO_BAG;
          const freeSampleCard: IFreeSampleCard = {
            productId: productId,
            tag: tag || '',
            productName: name,
            addToBagBtn: {
              text:
                showProductStatus == FREE_SAMPLE_STATUS.APPLIED
                  ? REMOVE
                  : ADD_TO_BAG,
              onClick: () =>
                handleAddRemoveProduct({
                  product: freeSample,
                  offerCount: {
                    offerClaimedCount,
                    offerEligibleCount,
                  },
                }),
              isDisabled:
                showProductStatus == FREE_SAMPLE_STATUS.NOT_APPLIED &&
                offerClaimedCount === offerEligibleCount,
            },
            image: {
              url: imageUrl,
            },
          };
          return freeSampleCard;
        }
      ),
    })
  );

  return (
    <PickYourFreeSamplesLayout data-test-id="pick_free_samples_screen">
      <PickFreeSamples
        offerFreeSamples={offerSamples}
        doneBtn={{
          text: DONE,
          onClick: togleShowPickFreeSamplesScreen,
        }}
      />
    </PickYourFreeSamplesLayout>
  );
};

export default PickFreeSample;
